import * as React from 'react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { PlannedBreakFragment, ShiftManagementPageDocument } from 'generated/resources/graphql'
import { isEmpty } from 'lodash'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import { dateBackendDateTimeToUIUtcTime } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

// eslint-disable-next-line factorial/no-design-system-long-imports
import Loading from 'modules/layout/components/Page/Loading'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import Form from 'modules/shift_management/components/Settings/SavedShifts/Form'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import { PermissionsProvider } from 'modules/shift_management/hooks/usePermissions'
import buildShiftsVisibility from 'modules/shift_management/lib/buildShiftsVisibility'
import { nameFromHexCode } from 'modules/shift_management/lib/colors'
import DefaultShiftForm from 'modules/shift_management/lib/forms/DefaultShiftForm'
import PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import { trackEditDefaultShiftSubmit } from 'modules/shift_management/lib/trackEvents'
import defaultShiftsCollection from 'modules/shift_management/store/api/collections/defaultShifts'
import DefaultShift from 'modules/shift_management/store/api/models/DefaultShift'
import { useBulkCudPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useBulkCudPlannedBreaks'
import { useGetPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useGetPlannedBreaks'

const plannedBreakForm = (plannedBreak: PlannedBreakFragment, isTimeFormat24H: boolean) => {
  return new PlannedBreakForm(
    {
      id: plannedBreak.id,
      start_at: dateBackendDateTimeToUIUtcTime(plannedBreak.startAt),
      end_at: dateBackendDateTimeToUIUtcTime(plannedBreak.endAt),
      break_type: plannedBreak.breakType,
      duration: plannedBreak.duration,
      'break_configuration.id': plannedBreak.breakConfiguration?.id,
      'break_configuration.name': plannedBreak.breakConfiguration?.name,
      'break_configuration.paid': plannedBreak.breakConfiguration?.paid,
      'break_configuration.archived': plannedBreak.breakConfiguration?.archived,
    },
    isTimeFormat24H
  )
}

const EditSavedShiftComponent = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const form = useLazyRef(() => new DefaultShiftForm()).current
  const {
    data: permissions,
    loading: loadingPermissions,
    error,
  } = useQuery(ShiftManagementPageDocument, {})
  const { isTimeFormat24H } = useIsTimeFormat24H()

  const { bulkCud } = useBulkCudPlannedBreaks()

  const params = useParams()
  const { id } = params

  const [defaultShift, setDefaultShift] = React.useState<DefaultShift | undefined>(undefined)

  const { loading } = useAsyncData(async () => {
    const fetchedDefaultShift = await defaultShiftsCollection.fetch({ data: { id: Number(id) } })

    if (isEmpty(fetchedDefaultShift)) {
      navigate(navigation.shifts.savedShifts.fullPath())
    } else {
      setDefaultShift(new DefaultShift(fetchedDefaultShift[0]))

      const teamIds = fetchedDefaultShift[0].visible_teams_ids?.length
        ? fetchedDefaultShift[0].visible_teams_ids
        : null
      const locationIds = fetchedDefaultShift[0].visible_locations_ids?.length
        ? fetchedDefaultShift[0].visible_locations_ids
        : null

      form.setValues({
        title: fetchedDefaultShift[0].title,
        color: fetchedDefaultShift[0].color,
        start_at: fetchedDefaultShift[0].start_at,
        end_at: fetchedDefaultShift[0].end_at,
        location_id: fetchedDefaultShift[0].location_id,
        work_area_id: fetchedDefaultShift[0].work_area_id,
        notes: fetchedDefaultShift[0].notes,
        all_company: fetchedDefaultShift[0].all_company,
        teams_ids_visibilities: teamIds,
        locations_ids_visibilities: locationIds,
        extra_hours: fetchedDefaultShift[0].extra_hours,
      })
    }
  }, [])

  const { plannedBreaks } = useGetPlannedBreaks({ variables: { defaultShiftIds: [+id!] } })

  // Setting plannedbreaks values into the form
  const [plannedBreakForms, setPlannedBreakForms] = useState(
    plannedBreaks.map((pb) => plannedBreakForm(pb, isTimeFormat24H))
  )

  useUnsafeEffect(() => {
    const forms = plannedBreaks.map((pb) => plannedBreakForm(pb, isTimeFormat24H))
    setPlannedBreakForms(forms)
  }, [plannedBreaks, isTimeFormat24H])

  const onSubmit = async () => {
    trackEditDefaultShiftSubmit({
      name: form.get('title').value,
      color: nameFromHexCode(form.get('color').value),
      startAt: form.get('start_at').value,
      endAt: form.get('end_at').value,
      workplace: form.get('location_id').value,
      workarea: form.get('work_area_id').value,
      comments: form.get('notes').value,
      allCompany: form.get('all_company').value,
      teamsIdsVisibilities: form.get('teams_ids_visibilities').value,
      locationsIdsVisibilities: form.get('locations_ids_visibilities').value,
    })

    const { teamsIdsVisibilities, locationsIdsVisibilities } = buildShiftsVisibility({
      form: form,
      isSingleSelector: false,
    })

    form.setValues({
      teams_ids_visibilities: teamsIdsVisibilities,
      locations_ids_visibilities: locationsIdsVisibilities,
    })

    const result = await asyncAction(
      async () => {
        if (!defaultShift) return
        await form.save(defaultShift)
        bulkCud({
          plannedBreaksParams: [
            {
              defaultShiftId: defaultShift.id,
              plannedBreaks: plannedBreakForms.map((pb) => ({
                startAt: pb.get('start_at').value,
                endAt: pb.get('end_at').value,
                duration: +pb.get('duration').value,
                breakType: pb.get('break_type').value,
                breakConfigurationId: +pb.get('break_configuration.id').value,
              })),
            },
          ],
        })
      },
      {
        successMessage: i18n.t('shift_management.saved_shifts.actions.edit.success'),
      }
    )
    if (isSuccessful(result)) {
      navigate(navigation.shifts.savedShifts.fullPath())
    }
  }

  if (loading || loadingPermissions) {
    return (
      <Box width="full" alignItems="center" height="full" justifyContent="center">
        <Loading />
      </Box>
    )
  }

  return (
    <PermissionsProvider permissions={permissions}>
      <ResponsivePage.Body loading={loadingPermissions} error={error}>
        <Form
          isEditing
          form={form}
          plannedBreakForms={plannedBreakForms}
          setPlannedBreakForms={setPlannedBreakForms}
          onSubmit={onSubmit}
        />
      </ResponsivePage.Body>
    </PermissionsProvider>
  )
}

const EditSavedShift = observer(EditSavedShiftComponent)

export default EditSavedShift
